<template>
    <div class="moon-dashboard">
  
      <!-- Header -->
      <section class="dashboard-header">
        <div class="header-content">
          <div class="welcome-text">
            <h1>Welcome1, {{ userName }}</h1>
            <p>Performance Overview</p>
          </div>
  
          <div class="stats-box">
            <div class="stat-item">
              <div class="stat-value">{{ currentOrders }}</div>
              <div class="stat-label">Total Orders</div>
            </div>
  
            <div class="stat-item">
              <div class="stat-value">AED {{ currentPayout }}</div>
              <div class="stat-label">Total Payout</div>
            </div>
          </div>
        </div>
      </section>
  
      <!-- Main Container -->
      <section class="dashboard-content">
  
        <!-- Tabs -->
        <div class="tabs">
          <button
            v-for="tab in tabs"
            :key="tab"
            :class="{ active: activeTab === tab }"
            @click="activeTab = tab">
            {{ tab }}
          </button>
        </div>
  
        <!-- Date Filter (Only Orders) -->
        <div v-if="activeTab === 'Orders'" class="date-filter">
          <label>Start:</label>
          <input type="date" v-model="dateFrom" />
  
          <label>End:</label>
          <input type="date" v-model="dateTo" />
  
          <button @click="applyDateRange">Apply</button>
          <span class="current-label">Default: Current Month</span>
        </div>
  
        <!-- My Coupons -->
        <div v-if="activeTab === 'My Coupons'" class="coupon-table">
          <table>
            <thead>
              <tr>
                <th>Advertiser</th>
                <th>Coupon</th>
                <th>Industry</th>
                <th>Status</th>
              </tr>
            </thead>
  
            <tbody>
              <tr v-for="coupon in couponsData" :key="coupon.id">
                <td>{{ coupon.company_name || '-' }}</td>
                <td>{{ coupon.coupon_code || '-' }}</td>
                <td>{{ coupon.Industry || '-' }}</td>
                <td>
                  <span class="status-dot" :class="{ active: coupon.coupon_status === 'Active' }"></span>
                  {{ coupon.coupon_status }}
                </td>
              </tr>
            </tbody>
          </table>
  
          <div v-if="!couponsData.length" class="no-coupons">No coupons available</div>
        </div>
  
        <!-- Orders Tab -->
        <div v-if="activeTab === 'Orders'" class="summary-table">
          <table>
            <thead>
              <tr>
                <th>Advertiser</th>
                <th>Coupon</th>
                <th>Revenue [AED]</th>
                <th>Conversions</th>
                <th>Payout [AED]</th>
              </tr>
            </thead>
  
            <tbody>
              <template v-for="(group, index) in groupedSummary">
  
                <tr
                  class="main-row"
                  :key="'main-' + index"
                  @click="toggleExpand(group.client_name)"
                >
                  <td class="advertiser">
                    <span class="toggle-icon">
                      {{ expanded[group.client_name] ? '▼' : '▶' }}
                    </span>
                    <img v-if="group.logo" :src="group.logo" class="brand-logo" />
                    {{ group.client_name }}
                  </td>
                  <td>—</td>
                  <td>AED {{ group.totalRevenue.toLocaleString() }}</td>
                  <td>{{ group.totalConversions }}</td>
                  <td>AED {{ group.totalPayout.toLocaleString() }}</td>
                </tr>
  
                <tr
                  v-for="(item, i) in group.items"
                  :key="'child-' + group.client_name + '-' + i"
                  v-show="expanded[group.client_name]"
                  class="child-row"
                >
                  <td></td>
                  <td>{{ item.coupon_code }}</td>
                  <td>AED {{ item.net_revenue_aed.toLocaleString() }}</td>
                  <td>{{ item.conversions }}</td>
                  <td>AED {{ item.net_payout_aed.toLocaleString() }}</td>
                </tr>
  
              </template>
            </tbody>
          </table>
  
          <div v-if="!groupedSummary.length" class="no-data">No data available</div>
        </div>
  
      </section>
    </div>
  </template>
  

<script>
import ApiService from "../../../core/services/api.service";

export default {
  name: "CustomDashboard",

  data() {
    return {
      userName: localStorage.getItem("username"),

      currentOrders: 0,
      currentPayout: 0,

      activeTab: "Orders",
      tabs: ["Orders", "My Coupons"],

      couponsData: [],

      groupedSummary: [],
      expanded: {},

      dateFrom: "",
      dateTo: "",
    };
  },

  mounted() {
    this.setDefaultDateRange();
    this.fetchSummary();
  },

  watch: {
    activeTab(newVal) {
      if (newVal === "My Coupons") this.fetchCoupons();
      if (newVal === "Orders") this.fetchSummary();
    },
  },

  methods: {
    setDefaultDateRange() {
      const today = new Date();
      this.dateFrom = new Date(today.getFullYear(), today.getMonth(), 1)
        .toISOString()
        .slice(0, 10);

      this.dateTo = new Date(today.getFullYear(), today.getMonth() + 1, 0)
        .toISOString()
        .slice(0, 10);
    },

    async applyDateRange() {
      await this.fetchSummary();
    },

    // FETCH COUPONS
    async fetchCoupons() {
      try {
        const response = await ApiService.post("/active-coupons", {
          contact_id: localStorage.getItem("contact_id"),
        });

        if (response.data.status === "Success") {
          this.couponsData = response.data.data.coupon || [];
        } else {
          this.couponsData = [];
        }
      } catch {
        this.couponsData = [];
      }
    },

    // UPDATE HEADER STATS
    updateMonthlyStats(data) {
      this.currentOrders = data.reduce((s, i) => s + (i.conversions || 0), 0);
      this.currentPayout = data.reduce((s, i) => s + (i.net_payout_aed || 0), 0);
    },

    // FETCH SUMMARY
    async fetchSummary() {
      try {
        const payload = {
          contact_id: localStorage.getItem("contact_id"),
          datefrom: this.dateFrom,
          dateto: this.dateTo,
        };

        const response = await ApiService.post("/v3/stats", payload);

        if (response.data.data.listing_data.conversions) {
          const rawData = response.data.data.listing_data.conversions;
          this.groupedSummary = this.groupByAdvertiser(rawData);
          this.updateMonthlyStats(rawData);
        } else {
          this.groupedSummary = [];
        }
      } catch {
        this.groupedSummary = [];
      }
    },

    // GROUP SUMMARY BY ADVERTISER
    groupByAdvertiser(data) {
      const grouped = {};

      data.forEach((item) => {
        const name = item.client_name || "Unknown";

        if (!grouped[name]) {
          grouped[name] = {
            client_name: name,
            logo: item.logo,
            items: [],
            totalRevenue: 0,
            totalConversions: 0,
            totalPayout: 0,
          };
        }

        grouped[name].items.push(item);
        grouped[name].totalRevenue += item.net_revenue_aed || 0;
        grouped[name].totalConversions += item.conversions || 0;
        grouped[name].totalPayout += item.net_payout_aed || 0;
      });

      return Object.values(grouped);
    },

    toggleExpand(name) {
      this.$set(this.expanded, name, !this.expanded[name]);
    },
  },
};
</script>


<style lang="scss">
    .bg-light {
        background: unset !important;
    }

    #kt_content .ddd button.btn.btn-secondary {
        display: block !important;
        margin-top: 15px;
        background: #000 !important;
        border-color: #000 !important;
        color: #fff;
    }

    .load-more, .load-more:hover {
        color: #fff;
        background-color: black;
        border-color: black;
        margin: 0% 40%;
        width: 20%;
    }

    .notify-popup-list {
        color: #3f4254 !important;
    }

    .notify-confirm-class {
        margin: 0px !important;
    }

    .notify-confirm-class .swal2-confirm.swal2-styled {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 20%;
    }

    .notify-confirm-class .swal2-cancel.swal2-styled {
        padding: 0px;
        display: flex !important;
        align-items: center;
        justify-content: center;
        background: transparent !important;
        position: absolute;
        top: 13px;
        right: 10px;
        margin-left: 15px;
        color: #000 !important;
        border-radius: 15px !important;
        font-size: 25px;
        font-weight: 600;
        width: 15px;
        height: 15px;
    }

    .h-notify-count {
        background: #f56160;
        color: #fff;
        border-radius: 10px;
        width: 25px;
        font-size: 12px;
    }

    .btn-mobile-profile ul {
        width: auto !important;
    }

    .dash-z-index {
        z-index: unset !important;
    }

    .table-responsive {
        display: inline-table !important;
    }

    .btn-div {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .update-btn {
        }

        .export-btn {
            button {
                background: linear-gradient(rgb(3, 3, 53), rgb(11, 28, 102));
                border: none;
                border-radius: 6px;
                font-size: 13px;
                color: #fff;
                padding: 7px 15px;
            }
        }
    }

    .offer-container {
        text-align: center !important;
        min-width: fit-content !important;
    }

    .offer-container .dropdown-item:active {
        background-color: #ebedf3 !important;
    }

    .offer-link:hover {
        background-color: transparent !important;
    }

    .offer-sort-btn {
        font-size: 13px !important;
        font-weight: normal !important;
        line-height: normal !important;
        color: inherit !important;
        background-color: transparent !important;
    }

    .offer-text {
        margin-right: 10px !important;
    }

    .offer-link {
        padding: 0.25rem 0.25rem !important;
    }

    .offer-link:hover {
        color: #0f2753 !important;
    }

    #filter_modal___BV_modal_body_ {
        padding: 20px;
        overflow: auto;
    }

    .invoiceLabels {
        width: 30%;
    }

    .invoiceLabels ul li {
        padding: 7.5px 7px;
        margin-right: 5px;
        cursor: pointer;
        border-top: 2px solid #949494;
        transition: 0.2s ease-in-out;
    }

    .invoiceLabels ul li:last-child {
        border-bottom: 2px solid #949494;
    }

    .invoiceLabels ul li:hover {
        background: #d5d5d5;
    }

    .invoiceLabels ul li.active {
        background: #383b5d;
        color: #fff;
    }

    .container-fluid {
        padding: 0px !important;
    }

    .table-div {
        // max-height: 420px;
        // overflow: hidden;
        // overflow-y:auto;
        table {
            thead {
                padding: 0px;
            }

            tbody {
                tr {
                    .long-column-bottom {
                        min-width: 180px;
                        font-weight: bold;
                        vertical-align: middle;
                    }

                    .long-column {
                        min-width: 180px;

                        // vertical-align: bottom;
                        h3 {
                            font-weight: bold;
                            margin: 0px;
                            font-size: 19px;
                        }
                    }

                    td {
                    }
                }
            }
        }
    }

    @media screen and (max-width: 768) {
        .table {
            border: none;
        }
    }

    @media screen and (max-width: 767.9px) {
        .table thead th {
            padding: 5px 10px;
        }

        .table thead th span {
            font-size: 10px;
            white-space: nowrap;
        }

        .table tbody tr td {
            //  word-break: break-all;
        }

        .display-none {
            display: none !important;
        }

        .mobile-data-grid div {
            margin: 0px 15px;
            // overflow: scroll;
        }

        .i-mobile-data-grid {
            margin: 0px 15px;
        }
    }

    @media screen and (max-width: 768) {
        .table tr {
            border-bottom: 3px solid #ddd;
            display: block;
            margin-bottom: 0.625em;
        }
    }

    @media screen and (max-width: 768) {
        .table thead {
            border: none;
            clip: rect(0 0 0 0);
            height: 1px;
            margin: -1px;
            overflow: hidden;
            padding: 0;
            position: absolute;
            width: 1px;
        }
    }

    @media only screen and (max-width: 768px) {
        .table-coupon thead {
            display: none;
        }

        .table-coupon tr {
            border-bottom: 15px solid #f0f4f7;
        }

        .table-coupon td {
            border-bottom: 1px solid #ddd;
            display: flex;
            display: -webkit-flex;
            display: -ms-flexbox;
            font-size: 0.8em;
            text-align: right;
        }

        .table-coupon td::before {
            content: attr(data-label);
            display: block;
            width: 100%;
            text-align: left;
            font-weight: bold;
            text-transform: uppercase;
        }

        .table-coupon td:last-child {
            border-bottom: 0;
        }
    }

    .body-content {
        position: unset !important;
    }

    #kt_content {
        min-height: 100vh !important;
        padding-top: 50px;
        height: unset !important;
    }

    .crone-alert {
        width: 100%;
        background-color: #000;
        color: #fff;
        padding: 5px 5px 5px 10px;
        border-radius: 5px;
        margin: 10px 0px;
        font-weight: 600;
    }

    .filterButtonGroup {
        display: flex;
        width: 100%;
    }

    .filterButtonGroup button {
        margin-right: 5px;
    }

    .badge {
        color: white;
        border-radius: 5px;
    }

    .new-tag-bg {
        background: #0253b0;
        border-color: #0253b0;
    }

    .comfirmed-tag-bg,
    .approved-tag-bg {
        background: #00c778;
        border-color: #00c778;
    }

    .cancelled-tag-bg {
        background: #707070;
        border-color: #707070;
    }

    .export_file {
        float: right;
        cursor: pointer;
    }

    .s_date {
        // margin-right: 4%;
        cursor: pointer;
    }

    .select {
        color: #000;
        font-weight: 700;
    }

    //   .plan-boxes {
    //     display: flex;
    //     align-items: center;
    //     justify-content: center;
    //   }
    // }

    // @media only screen and (max-width: 600px) {
    //   .plan-box {
    //     height: 40%;
    //     position: relative;
    //   }
    //   .logo{
    //     width: 80% !important;
    //   }
    // }
    .modal-btn {
        width: 100%;
        color: white;
        background: #000;
        border-radius: 0px;
    }

    .tabs {
        display: flex;
        text-align: center;
        font-weight: 600;
        border-bottom: 1px solid #e8e1e1;

        .tab {
            cursor: pointer;
        }

        .active {
            border-bottom: 1.5px solid black;
        }
    }

    @media only screen and (max-width: 450px) {
        .tabs {
            display: table !important;
            margin: auto;
        }
    }

    // for destop
    @media only screen and (min-width: 1200px) {
        .plan-boxes {
            span:first-of-type {
                margin-bottom: 7%;
            }
        }

        .plan-box {
            background-color: #ffffff;
            min-height: 200px;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;

            span:first-of-type {
                display: block;
                text-transform: uppercase;
                color: #000;
                font-weight: bold;
                font-size: 16px;
            }

            h1 {
                font-weight: bold;
                color: #000;
                font-size: 4rem;
                margin: 10px 0;
            }

            span:last-of-type {
                display: block;
                text-transform: capitalize;
                color: #000;
                font-weight: bold;
                font-size: 16px;
            }
        }
    }

    @media only screen and (min-width: 1200px) {
        .table thead th,
        .table tbody td {
            white-space: unset;
        }

        .last_refreshed {
            color: black !important;

            // margin-top: 220px;
            icon {
                color: white;
            }

            span {
                margin-left: 1rem;
            }
        }
    }

    .ddd {
        margin-top: -2.5rem !important;
    }

    @media only screen and (max-width: 1200px) {
        .last_refreshed {
            color: #19248d;
            margin-top: 0px !important;

            span {
                margin-left: 1rem;
            }
        }
    }

    @media only screen and (max-width: 1600px) {
        .last_seen {
            color: white;
            float: right;

            span {
                margin-left: 1rem;
            }
        }
    }

    //for screens bigger then mobile
    @media only screen and (min-width: 1600px) {
        .last_seen {
            color: black;
            float: right;

            span {
                margin-left: 1rem;
            }
        }
    }

    // for mobile
    @media only screen and (max-width: 1200px) {
        .offer-text {
            margin-right: 0px !important;
        }

        .plan-boxes {
            margin-bottom: 5%;
            display: inline;

            span:last-of-type {
                float: right;
            }
        }

        .plan-box {
            background-color: #ffffff;
            width: 45%;
            min-height: 100px;
            text-align: center;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;

            span:first-of-type {
                display: block;
                text-transform: uppercase;
                color: #000;
                font-weight: bold;
                font-size: 12px;
            }

            h1 {
                font-weight: bold;
                color: #000;
                font-size: 2rem;
                margin: 10px 0;
            }

            span:last-of-type {
                display: block;
                text-transform: capitalize;
                color: #000;
                font-weight: bold;
                font-size: 12px;
            }
        }

        #kt_content .ddd {
            display: none;
        }

        .header-mobile-fixed .header-mobile {
            height: 350px !important;
        }

        #kt_content .container-fluid {
            margin-top: -240px;
            position: relative;
            // z-index: 99;
            z-index: unset !important;
        }

        .plan-box span:last-of-type {
            font-size: 16px;
        }

        .plan-box h1 {
            font-size: 3rem;
        }

        .btn-div.mt-5.mobile {
            display: flex !important;
            justify-content: space-between !important;
            width: 100%;
            padding: 0 10px 0 0;
        }

        .btn-div.mt-5.mobile button.btn.btn-secondary {
            background: transparent;
            border: 0;
        }

        .dropdown.mobile.d-lg-none.d-block.p-2 {
            display: none !important;
        }
    }

    .light_gray {
        color: rgb(181, 181, 181);
        padding: 0px;
    }

    .toggle_buttons {
        border: 2px solid rgb(121, 121, 121) !important;
        border-radius: 5px !important;
        font-size: 1rem !important;
        font-weight: bold !important;
    }

    @media only screen and (max-width: 600px) {
        .toggle_buttons {
            width: 6rem !important;
            font-size: 0.8rem !important;
        }
    }

    @media only screen and (max-width: 450px) {
        .toggle_buttons {
            width: 7rem !important;
            height: 4rem !important;
            font-size: 0.85rem !important;
            margin: 0.1rem !important;
        }
    }

    @media only screen and (max-width: 363px) {
        .toggle_buttons {
            width: 6rem !important;
            height: 2.5rem !important;
            font-size: 0.7rem !important;
        }
    }

    @media only screen and (max-width: 305px) {
        .toggle_buttons {
            width: 4rem !important;
            height: 3rem !important;
            font-size: 0.5rem !important;
        }
    }

    @media only screen and (max-width: 450px) {
        .table_res_head {
            display: none !important;
        }
    }

    #subparent .tab_buttons {
        border: none !important;
        border-radius: 5px !important;
        color: white;
        background-color: #ff6633 !important;
    }

    #parent .tab_buttons {
        border: none !important;
        border-radius: 5px !important;
        color: #000;
        background-color: #ff6633 !important;
    }

    .active_status {
        // background-color: #00c778;
        //   padding: 0.2rem 1.8rem;
        padding: 2px 10px;
        border-radius: 0.3rem;
        width: 100%;
        text-align: center;
    }

    .expired_status {
        // background-color: #f59253;
        //   padding: 0.2rem 1.8rem;
        padding: 3px 5px;
        border-radius: 0.3rem;
        width: 100%;
        text-align: center;
    }

    .status_coupon {
        color: rgb(163, 137, 137);
        border-radius: 0.3rem;
    }

    .modal-content {
        bottom: unset !important;
    }

    @media (max-width: 767px) {
        .bar span {
            font-size: 11px;
        }
    }

    // Changes

    div#filter_modal___BV_modal_outer_ {
        z-index: 9999999 !important;
    }

    @media (min-width: 1200px) {
        p.last_refreshed {
            color: #000 !important;
            // margin-top: 110px !important;
            position: relative;
            z-index: 99;
            padding-left: 0;
            font-weight: normal;
            font-size: 20px;
            margin-bottom: 8px;
            width: 70%;
        }

        p.last_refreshed + .btn-div.mt-5 {
            position: relative;
            z-index: 999;
            padding-left: 0;
            margin-top: 0 !important;
            width: 70%;
        }

        p.last_refreshed + .btn-div.mt-5 .export-btn {
            // display: none;
        }

        p.last_refreshed + .btn-div.mt-5 .update-btn button {
            font-size: 12px;
            padding: 6px 10px;
            border-radius: 6px;
        }
        .content-wrapper {
            overflow: scroll;
        }
        .content-wrapper > div > .row {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            margin-bottom: 0 !important;
        }

        .content-wrapper .plan-boxes {
            max-width: none !important;
            flex: none;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            margin-top: -93px;
            position: relative;
            // z-index: 999;
            width: 80%;
            margin-left: 20%;
            margin-bottom: 17px;
        }

        .content-wrapper .plan-boxes + div {
            max-width: 100% !important;
            flex: none;
            position: relative;
            z-index: 999;
        }

        .plan-boxes span {
            min-height: auto;
            min-width: 140px;
            padding: 5px;
            margin-bottom: 0 !important;
        }

        .plan-box h1 {
            margin: 0;
            font-size: 30px;
        }

        span.plan-box:first-of-type {
            margin-right: 10px;
        }

        #kt_content > div > .container-fluid {
            max-width: 1185px;
            width: 100%;
        }

        .inf-table {
            padding-top: 0 !important;
        }

        .v-btn:before {
            display: none;
        }

        .v-btn-toggle > .v-btn.v-btn {
            opacity: 1 !important;
            height: 40px !important;
            border-width: 1px !important;
        }

        .inf-table p.table-head {
            font-size: 18px;
            color: #000;
        }

        .inf-table .tabs {
            border: 0;
        }

        .inf-table p.table-head span {
            font-size: 10px;
            position: relative;
            top: -10px;
            opacity: 0.3;
        }

        .data_grid {
            margin-top: 0 !important;
        }

        .table thead th,
        .table tbody td {
            border: 0 !important;
        }

        .table-div .table-bordered {
            border: 0 !important;
        }

        .modal .modal-header h4 {
            color: #000;
            font-weight: bold;
            font-size: 20px;
        }

        .modal .modal-header {
            border: 0;
            padding-bottom: 0;
        }

        div#filter_modal___BV_modal_body_ > .row > .col-12:first-child {
            color: #000;
            font-weight: bold;
        }

        div#filter_modal___BV_modal_body_ > .row label {
            font-weight: bold;
            color: #000;
        }

        div#filter_modal___BV_modal_body_ > .row > div input {
            color: #000;
        }

        .update-btn span {
            font-size: 11px;
            color: #000;
        }

        .update-btn span i {
            color: #000;
            font-size: 11px;
            margin-left: 5px;
            -webkit-transform: rotateY(160deg) rotate(12deg);
            transform: rotateY(160deg) rotate(12deg);
        }

        tr.offers {
            display: none;
        }

        .active_status {
            // background-color: #00c778;
            padding: 0px 15px 1px;
            width: auto;
            text-align: center;
            // color: #fff;
            line-height: normal;
            border-radius: 0;
            font-weight: normal;
        }

        .expired_status {
            padding: 0px 5px 1px;
            width: auto;
            text-align: center;
            // color: #fff;
            line-height: normal;
            border-radius: 0;
            font-weight: normal;
        }

        .table thead th {
            text-align: left !important;
            font-size: 12px !important;
            font-weight: normal;
        }

        td.for-desk {
            width: 200px;
        }

        td.advertise {
            width: 250px;
        }

        .data_grid table {
            border: 0;
        }

        .data_grid table tr td {
            font-weight: bold;
        }

        .content-wrapper .plan-boxes {
            margin-bottom: 29px;
        }

        // div#filter_modal___BV_modal_body_ > .row > div {
        //     margin-bottom: 24px;
        // }
        div#filter_modal___BV_modal_outer_ footer#filter_modal___BV_modal_footer_ {
            padding-top: 0;
        }

        .v-card.v-sheet.theme--light .v-list-item.theme--light {
            padding-left: 0;
        }

        p.last_refreshed + .btn-div.mt-5 .update-btn button {
            display: block !important;
            margin-top: 15px;
        }

        .update-btn {
            display: flex;
            flex-direction: column-reverse;
        }

        div#modal-1___BV_modal_outer_ {
            z-index: 9999999 !important;
        }

        .table thead th {
            width: 200px;
        }

        .table tbody td {
            text-align: left;
        }

        input.overlay-hide-radio {
            display: none !important;
        }
        .hide {
            visibility: hidden !important;
        }

        .overlay {
            display: none !important;
        }
    }

    form.form .btn.btn-dark.form-control {
        margin-top: 20px;
    }

    .form-group label {
        font-size: 13px !important;
        font-weight: bold !important;
        color: #000 !important;
    }

    footer#filter_modal___BV_modal_footer_ .modal-btn.btn-dark {
        border-radius: 6px;
    }

    @media (max-width: 1200px) {
        #kt_content .ddd {
            display: none;
        }

        .header-mobile-fixed .header-mobile {
            height: 350px !important;
        }

        #kt_content .container-fluid {
            margin-top: -240px;
            position: relative;
            z-index: 99;
        }

        .plan-boxes .plan-box {
            padding: 0 10px;
        }

        .plan-box span:last-of-type {
            font-size: 16px;
        }

        .plan-box h1 {
            font-size: 3rem;
        }

        .btn-div.mt-5.mobile {
            display: flex !important;
            justify-content: space-between !important;
            width: 100%;
            padding: 0 10px 0 0;
        }

        .btn-div.mt-5.mobile button.btn.btn-secondary {
            background: transparent;
            border: 0;
        }

        .dropdown.mobile.d-lg-none.d-block.p-2 {
            display: none !important;
        }

        .v-btn-toggle--group > .v-btn.v-btn {
            margin: 0 !important;
            padding: 0 !important;
            border: 0 !important;
            border-radius: 0 !important;
            min-width: auto !important;
            width: 80px !important;
            font-size: 13px !important;
            display: block !important;
            margin-right: 18px !important;
            border-bottom: 2px solid transparent !important;
            margin-right: 6px !important;
            padding-bottom: 5px;
            color: #8080808c !important;
        }

        #parent .tab_buttons {
            color: #151a4b !important;
            background-color: transparent !important;
            min-width: auto !important;
            width: 80px !important;
            font-size: 13px !important;
            border-bottom: 2px solid #ff6633 !important;
            border-radius: 0 !important;
            font-weight: bold !important;
        }

        .v-btn:before {
            display: none;
        }

        .v-btn-toggle--group {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto;
            width: 270px !important;
            padding: 0 0;
            padding-bottom: 5px;
        }

        .v-btn__content {
            display: inline-block;
        }

        .modal-content {
            bottom: 0 !important;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        .data_grid .alert.alert-primary {
            width: 90%;
            margin: 10px auto;
        }

        .table tbody tr td {
            font-weight: bold;
        }

        .table tbody tr.offers td {
            font-weight: normal !important;
            opacity: 0.8;
        }

        .active_status {
            // color: #fff;
            // padding: 0px 5px !important;
            width: auto;
        }

        .expired_status {
            // color: #fff;
            padding: 0px 3px;
        }

        #parent .toggle_buttons {
            height: 36px !important;
            width: auto !important;
            margin-right: 30px !important;
        }

        .table thead th {
            vertical-align: top;
            font-size: 13px !important;
        }

        header#filter_modal___BV_modal_header_ > h4 {
            font-size: 22px;
            color: #000;
            font-weight: bold;
        }

        div#filter_modal___BV_modal_body_ > .row .col-12:first-child {
            font-weight: bold;
            font-size: 14px;
            color: #000;
        }

        div#filter_modal___BV_modal_body_ > .row .col-12 label {
            font-weight: bold;
            font-size: 14px;
        }

        th.light_gray {
            text-align: left !important;
        }

        div#filter_modal___BV_modal_body_ > .row > div:nth-child(2) {
            padding-right: 0;
            -ms-flex: 0 0 42.666667%;
            flex: 0 0 45.666667%;
            max-width: 45.666667%;
        }

        div#filter_modal___BV_modal_body_ > .row > div:nth-child(3) {
            padding-left: 0;
            padding-right: 0;
            /*-ms-flex: 0 0 15.666667%;*/
            /*flex: 0 0 12.666667%;*/
            /*max-width: 12.666667%;*/
        }

        div#filter_modal___BV_modal_body_ > .row > div:nth-child(4) {
            padding-right: 0;
            padding-left: 0;
        }

        div#filter_modal___BV_modal_body_ {
            padding-bottom: 0;
            padding-top: 0;
        }

        header#filter_modal___BV_modal_header_ {
            padding-bottom: 10px;
        }

        div#filter_modal___BV_modal_body_ > .row .col-12:first-child {
            padding-bottom: 0;
        }

        .plan-box {
            width: 47%;
        }

        .plan-box h1 {
            margin-top: 0;
        }

        .plan-box span:last-of-type {
            font-size: 15px;
            padding-bottom: 5px;
        }

        .table thead th,
        .table tbody td {
            border: 0 !important;
        }

        .table-div .table-bordered {
            border: 0 !important;
        }

        th.light_gray.desk {
            display: none;
        }
        /// Added by devangi to show coupon for mobile view
        td.for-desk:nth-child(3) {
            display: none;
        }
        td.for-desk:nth-child(2) {
            display: block !important;
        }

        .tabs {
            border: 0;
        }

        .inf-table {
            padding-top: 0 !important;
        }

        .tabs {
            padding-bottom: 0 !important;
        }

        .data_grid table.table {
            margin-top: 10px !important;
            border: 0;
        }

        #kt_content .container-fluid {
            margin-top: -288px;
        }

        .inf-table .tabs {
            padding-bottom: 0 !important;
        }

        .data_grid {
            margin-top: 20px !important;
        }

        div#filter_modal___BV_modal_body_ > .row > div {
            margin-bottom: 18px;
        }

        .modal {
            z-index: 999999999;
        }

        #kt_content .ddd {
            display: block;
            padding-left: 8px;
        }

        #kt_content .ddd p.last_refreshed {
            display: none;
        }

        #kt_content .ddd .export-btn {
            // display: none;
        }

        #kt_content .ddd span {
            display: none;
        }

        #kt_content .ddd button.btn.btn-secondary {
            display: block !important;
            margin-top: 15px;
            background: #000 !important;
        }

        div#modal-1___BV_modal_content_ {
            height: 100vh;
            overflow-y: auto;
        }

        .header-mobile-fixed .header-mobile {
            height: 410px !important;
        }

        #kt_content .container-fluid {
            margin-top: -286px;
            position: relative;
            z-index: 99;
            width: 100%;
            padding-left: 1rem !important;
            padding-right: 1rem !important;
        }

        div#modal-1___BV_modal_outer_ {
            position: static !important;
            height: 100%;
            z-index: 99999 !important;
        }

        div#modal-1___BV_modal_outer_ .modal-dialog.modal-md {
            height: 100%;
            max-width: 100% !important;
            width: 100% !important;
            margin: 0 !important;
            pointer-events: auto !important;
            position: static !important;
        }

        div#modal-1___BV_modal_outer_ header {
            pointer-events: auto;
        }

        div#filter_modal___BV_modal_outer_ {
            position: static !important;
            height: 100%;
            width: 100%;
        }

        div#filter_modal___BV_modal_outer_ .modal-dialog.modal-md {
            max-width: 100% !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 9999;
            display: flex;
            margin: 0;
            align-items: flex-end;
        }

        div#filter_modal___BV_modal_outer div#filter_modal___BV_modal_content {
            position: static;
            height: 90%;
            overflow-y: auto;
        }

        div#filter_modal___BV_modal_outer div#filter_modal___BV_modal_content footer {
            border: 0;
        }

        div#filter_modal___BV_modal_content_ {
            position: static;
            height: 90%;
            overflow-y: auto;
        }

        .table tbody td {
            width: auto !important;
            text-align: left;
        }

        .table thead th {
            width: 200px;
        }

        .data_grid table tbody tr ~ tr.offers ~ tr:not(.offers) {
            border-bottom: 0;
        }

        .dash-table table tbody tr.couponClass:first-child {
            border-bottom: 5px solid #fff !important;
        }

        #kt_content > .d-flex.flex-column-fluid {
            top: 0 !important;
        }
    }

    .v-btn--plain:not(.v-btn--active):not(.v-btn--loading):not(:focus):not(:hover)
    .v-btn__content {
        opacity: 1;
    }

    div#modal-1___BV_modal_content_ button.close {
        font-size: 40px;
        line-height: normal;
        opacity: 1;
    }

    .mx-datepicker-popup {
        z-index: 9999999 !important;
    }

    .d_data_grid {
        overflow-y: auto !important;
    }

    .data_grid .v-card.v-sheet.theme--light {
        box-shadow: none !important;
    }

    // .inf-table table.table.table-bordered {
    //     width: 100%;
    // }

    .inf-table table.table.table-bordered tbody tr td:nth-child(2) {
        text-align: left;
    }

    .fltr span span span {
        font-family: Gilory, Helvetica, Arial;
        font-weight: normal;
    }

    .inf-table .tabs {
        padding-bottom: 10px !important;
    }

    .data_grid table {
        margin-top: 10px !important;
        background: #fff2f2;
        height: auto;
    }

    .data_grid table tbody tr {
        background: white;
        border-bottom: 5px solid #fff2f2;
    }

    .data_grid table tbody tr td {
        vertical-align: middle;
    }

    .active_status {
        background: transparent;
        color: #3f4254;
        position: relative;
        padding-left: 15px !important;
    }

    .expired_status {
        background: transparent;
        color: #3f4254;
        position: relative;
        padding-left: 15px !important;
        width: auto;
    }

    .active_status:before {
        content: "";
        display: block;
        width: 8px;
        height: 8px;
        position: absolute;
        top: 5px;
        left: 2px;
        background: #00c778;
        border-radius: 50%;
        box-shadow: 0 0 3px #00c778;
    }

    .expired_status:before {
        content: "";
        display: block;
        width: 8px;
        height: 8px;
        position: absolute;
        top: 5px;
        left: 2px;
        background: red;
        border-radius: 50%;
        box-shadow: 0 0 3px red;
    }

    .table thead th:last-child {
        text-align: right !important;
    }

    .data_grid table tbody tr td:last-child {
        text-align: right !important;
    }

    .active_status {
        padding-right: 0;
    }

    label.expired_status {
        padding-right: 0;
    }

    .data_grid table tbody tr.offers td {
        text-align: left !important;
    }

    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        z-index: 99;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #ffff;
    }

    .overlay p {
        margin: 0;
        font-size: 25px;
    }

    .overlay img {
        width: 80px;
        margin: 0 auto;
        margin-bottom: 10px;
        animation-name: anim;
        animation-duration: 1s;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
        animation-direction: alternate;
    }

    .overlay-inner {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    input.overlay-hide-radio {
        position: absolute;
        top: -143px;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        z-index: 9999999;
    }

    input.overlay-hide-radio:checked {
        display: none;
    }

    input.overlay-hide-radio:checked + div {
        display: none;
    }

    @keyframes anim {
        0% {
            transform: translateX(-50%);
        }

        100% {
            transform: translateX(50%);
        }
    }

    #kt_content > .d-flex.flex-column-fluid {
        position: relative;
        top: 100px;
        margin-bottom: 100px;
        // z-index: 99999;
    }

    .page-loader {
        width: 100%;
        height: 100%;
        z-index: 999999;
        justify-content: center;
        top: -10px;
    }

    body.modal-open {
        overflow: initial !important;
    }

    .modal.fade .modal-dialog {
        -webkit-transform: none !important;
        transform: none !important;
    }

    .swal2-container.swal2-center {
        z-index: 999999;
    }

    .swal2-styled.swal2-confirm {
        color: #ff6633 !important;
    }

    .notify-container-class {
        display: flex;
        z-index: 1 !important;
        background: rgba(0, 0, 0, 0.5) !important;
    }

    .notify-popup-class {
        padding: 0px !important;
        z-index: 99999999999 !important;
        border-radius: 0.3125em;
        overflow: hidden;
    }

    .notify-header-class {
        display: block !important;
        padding: 20px !important;
        background: #ebebeb !important;
        border-bottom: 1px solid #121212;
    }

    .notify-title-class {
        margin: 0px !important;
        color: #5f5c62 !important;
        font-weight: 600 !important;
        font-size: 1.5rem;
    }

    .notify-content-class {
        margin: 0px !important;
        padding: 0px !important;
        text-align: left;
    }

    .notify-message-class {
        padding: 20px !important;
        border-bottom: 1px solid #f4f5f8;
        text-align: left !important;
    }

    .notify-message-class .bold {
        font-weight: 600;
    }

    .custom-zIndex {
        z-index: 0;
    }

    .plan-box {
        background: #000 !important;
        color: #fff !important;
    }

    .plan-box span:last-of-type,
    .plan-box h1 {
        color: #fff !important;
    }

    .d_data_grid a {
        color: #ff6633;
    }

    .offers-icon {
        width: 25px;
        height: 25px;
    }

    .btn.btn-primary {
        color: #000;
        background-color: #ff6633;
        border-color: #ff6633;
    }
</style>
